import BigNumber from 'bignumber.js';
import {
  StakingPool,
  UserPoolInfo,
  DepositCalculation,
  WalletRewardsInfo,
} from 'api/StakingAPI';
import { calculateAPR } from 'utils/aprCalculation';

export const calculateSDAOREP = (stakedAmount: string, duration: string) => {
  try {
    const stakedAmountBN = new BigNumber(stakedAmount);
    const durationBN = new BigNumber(duration);
    if (stakedAmountBN.isZero() || durationBN.isZero()) {
      return new BigNumber(0);
    }
    const srep = stakedAmountBN.multipliedBy(durationBN);
    return srep;
  } catch (error) {
    console.error(`Error calculating REP ${error}`);
    return new BigNumber(0);
  }
};

export const calculateREP = (stakedAmount: string, duration: string) => {
  return calculateSDAOREP(stakedAmount, duration);
};

export const calculateStake = (
  pool: StakingPool,
  userPoolInfo: UserPoolInfo,
  walletRewardsInfo: WalletRewardsInfo | undefined,
  tokenAmount: string,
  lockedPeriod: number,
  blockTimestamp: number
): DepositCalculation => {
  try {
    const { totalStaked, lockDate, unlockDate, claimable } = userPoolInfo;

    const totalStakedBN = new BigNumber(totalStaked);
    const sdaoInputBN = new BigNumber(tokenAmount);
    const blockTimestampBN = new BigNumber(blockTimestamp);
    const lockedPeriodBN = new BigNumber(lockedPeriod);
    const reputationScoreBN = new BigNumber(userPoolInfo.reputationScore);

    let deltaStore = new BigNumber(0);
    if (!sdaoInputBN.isZero()) {
      deltaStore = calculateREP(
        sdaoInputBN.toFixed(),
        lockedPeriodBN.toFixed()
      );
    }

    const newUnlockDateBN = BigNumber.max(
      new BigNumber(unlockDate).plus(lockedPeriodBN),
      blockTimestampBN.plus(lockedPeriodBN)
    );

    if (!totalStakedBN.isZero()) {
      const extensionPeriod = newUnlockDateBN.minus(unlockDate);

      deltaStore = deltaStore.plus(
        calculateREP(totalStakedBN.toFixed(), extensionPeriod.toFixed())
      );
    }

    const newUserRepOnGivenPool = reputationScoreBN.plus(deltaStore);

    const differenceInOldAndNewUserRep =
      newUserRepOnGivenPool.minus(reputationScoreBN);

    const totalReputationScoreOfGivenPool = new BigNumber(
      pool.totalReputationScore
    ).plus(differenceInOldAndNewUserRep);

    const newStaked = totalStakedBN.plus(sdaoInputBN);

    const aprInputs = {
      userREP: newUserRepOnGivenPool.toFixed(),
      totalReputationScoreOfPool: totalReputationScoreOfGivenPool.toFixed(),
      totalRewards: pool.totalRewards,
      rewardsEmissionPeriod: pool.rewardsEmissionPeriod,
      rewardsCommissionPercentage: pool.rewardsCommissionPercentage,
      userAmountStaked: newStaked.toFixed(),
    };
    const aprResult = calculateAPR(aprInputs);
    const apr = aprResult.isValid ? aprResult.apr : '0';

    const existingREPAcrossPools = walletRewardsInfo?.srep || '0';

    const tentativeUserRepOnAllPools = new BigNumber(existingREPAcrossPools)
      .plus(differenceInOldAndNewUserRep)
      .toFixed();

    return {
      apr: apr,
      claimable: claimable,
      existingREPAcrossPools: existingREPAcrossPools,
      lockDate: lockDate,
      tentativeREP: tentativeUserRepOnAllPools,
      totalStaked: newStaked.toFixed(),
      unlockDate: newUnlockDateBN.toFixed(),
      walletAddress: userPoolInfo.walletAddress,
      contractAddress: userPoolInfo.contractAddress,
    };
  } catch (error) {
    console.error(`Failed to Calculate Stake: ${error}`);
    throw error;
  }
};
